// @react
import React, { ReactElement } from 'react'
// @graphQL
import { useQuery } from '@apollo/client'
import { GET_ORDERS } from 'graphql/queries/order.query'
import {
  GetOrdersQuery,
  GetOrdersQueryVariables,
  OrderType,
} from 'lib/graphql/graphqlTypes'
// @common
import strings from 'lib/constants/strings'
import config from 'constants/config'
import usePagination from 'modules/common/hooks/usePagination'
import Helper from 'utils/Helper.class'
// @components
import OrderCard from '__COMPONENTS_TO_MIGRATE/CardNew/Variants/Orders'
import { PropsType as CardProps } from '__COMPONENTS_TO_MIGRATE/CardNew/Variants/Orders'
import PaginationWrapper from 'modules/common/components/PaginationWrapper'
import Spacer from '@bit/atd.web.spacer'
import QueryError from 'modules/common/components/Errors/QueryError'
import ConditionalLoader from '@bit/atd.web.loaders.conditional-loader'

type PropsType = {
  setCount: (count: number) => void
  timeLimit?: number
  searchKeyword: string
}

const DigitalOrders = ({
  setCount,
  timeLimit,
  searchKeyword,
}: PropsType): ReactElement => {
  const { Pagination, paginationProps } = usePagination()
  // GRAPHQL
  const { loading: isLoading, error: isError, data } = useQuery<
    GetOrdersQuery,
    GetOrdersQueryVariables
  >(GET_ORDERS, {
    variables: {
      input: {
        page: paginationProps.currentPage,
        pageSize: config.pageSize,
        filters: {
          orderType: OrderType.DIGITAL,
          dateRange: timeLimit,
          search: searchKeyword,
        },
      },
    },
  })

  if (isError) {
    return <QueryError />
  }

  const count = data?.orders?.meta?.count
  setCount(count || 0)

  const renderContent = () => {
    if (data?.orders.data && data?.orders.data.length > 0) {
      return (
        <>
          {data?.orders.data.map((item, index) => {
            const totalItems = data?.orders.data.length
            const lineItems: CardProps['lineItems'] = item?.lineItems?.map(
              (lineItem) => {
                return {
                  url: lineItem.itemUrl,
                  downloadURL: lineItem?.downloadUrl || '',
                  imageURL: lineItem?.imageUrl || '',
                  title: lineItem.title,
                  type: lineItem.type,
                  price: lineItem.price,
                }
              },
            )

            return (
              <React.Fragment key={`${item.__typename}_${item.id}`}>
                <OrderCard
                  date={Helper.smallDateFormatter(item?.date)}
                  orderNumber={item?.orderNumber}
                  total={item?.totalPrice}
                  shippedTo={item?.shippedTo || ''}
                  receiptUrl={item?.receiptUrl || ''}
                  lineItems={lineItems}
                />
                {index + 1 !== totalItems && <Spacer size={24} fullWidth />}
              </React.Fragment>
            )
          })}

          <PaginationWrapper>
            <Pagination
              {...paginationProps}
              count={data.orders.meta?.totalPages}
            />
          </PaginationWrapper>
        </>
      )
    }
    return (
      <div>
        <h3>{strings.PROFILE.ORDERS.NO_ORDERS}</h3>
      </div>
    )
  }

  return (
    <>
      <ConditionalLoader condition={!isLoading}>
        {renderContent()}
      </ConditionalLoader>
    </>
  )
}

export default DigitalOrders
